import axios from "axios";
import { fetchAuthSession } from "aws-amplify/auth";
import { useAuthStore } from "@/store/authStore";
import { extractApiError } from "@/api/error";

/**
 * Single axios instance for every backend call. Interceptor grabs the current
 * Cognito ID token from Amplify's token provider (in-memory cache backed by
 * localStorage) and attaches it as a bearer on every outbound request.
 *
 * Also attaches `X-Acting-As-Client` when a SuperAdmin is impersonating a
 * client tenant (authStore.actingAs). The backend honours this header only
 * for SuperAdmin (defence-in-depth on both sides) — see
 * monitoring-backend app/dependencies.get_permission_scope.
 *
 * MV2-1250: `X-View-As-User` when a SuperAdmin is viewing as a real user
 * (authStore.viewingAs) — the backend answers as that user, read-only
 * (backend.md §108).
 *
 * 401 handling: a response interceptor will (next iteration) force a token
 * refresh via `fetchAuthSession({ forceRefresh: true })` and retry once.
 * For the first cut we let 401s bubble so the router pushes back to /login.
 */
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 15_000,
});

/**
 * Per-request opt-out for the acting-as header. Set on the axios request
 * config when a call should hit the unscoped view (currently used by
 * TenantSwitcher's client list so the dropdown keeps showing every
 * client the SuperAdmin can impersonate even after they pick one — R3
 * fix). Typed via module augmentation so consumers get autocomplete.
 */
declare module "axios" {
  interface AxiosRequestConfig {
    skipActingAs?: boolean;
    /** MV2-1250: send this request as yourself, not the viewed user. */
    skipViewAs?: boolean;
  }
}

apiClient.interceptors.request.use(async (config) => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (idToken) {
      config.headers.Authorization = `Bearer ${idToken}`;
    }
  } catch {
    // Not signed in yet — let the request go. The backend will 401 and the
    // component will surface it / redirect as needed.
  }
  // Read fresh from the store each request (store subscription would require a
  // React context; axios interceptors live outside the React tree).
  const actingAs = useAuthStore.getState().actingAs;
  if (actingAs && !config.skipActingAs) {
    config.headers["X-Acting-As-Client"] = actingAs.clientId;
  }
  const viewingAs = useAuthStore.getState().viewingAs;
  if (viewingAs && !config.skipViewAs) {
    config.headers["X-View-As-User"] = viewingAs.userId;
  }
  return config;
});

// MV2-192: rewrite `AxiosError.message` to the backend envelope's
// `error.message` so existing `err instanceof Error ? err.message : ...`
// callsites surface the real reason (e.g. "tariff overlaps with existing
// tariff '...'", "folder is not empty (3 files)") instead of the axios
// default "Request failed with status code N". Status / response / config /
// stack are left untouched for callers that branch on them.
apiClient.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (error && typeof error === "object" && "message" in error) {
      const friendly = extractApiError(error);
      if (friendly && friendly !== "Request failed.") {
        (error as { message: string }).message = friendly;
      }
    }
    return Promise.reject(error);
  },
);
